// import { motion } from "framer-motion";
// import { Trophy } from "lucide-react";
// import { AWARDS } from "@/data/content";

// export default function Awards() {
//   return (
//     <section
//       id="awards"
//       data-testid="awards-section"
//       className="py-20 md:py-28 bg-[#0F2D52] text-white relative overflow-hidden"
//     >
//       <div className="absolute inset-0 -z-0">
//         <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
//         <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#1E5AA8]/20 blur-3xl" />
//       </div>

//       <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="max-w-2xl">
//           <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">
//             Recognition
//           </span>
//           <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading tracking-tight text-white">
//             Awards from our banking partners
//           </h2>
//           <p className="mt-3 text-sm text-white/70">
//             Recognition received from partner banking institutions for consistent performance and quality of files facilitated.
//           </p>
//         </div>

//         <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
//           {AWARDS.map((a, i) => (
//             <motion.div
//               key={a.title}
//               initial={{ opacity: 0, y: 16 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               viewport={{ once: true, margin: "-80px" }}
//               transition={{ duration: 0.5, delay: i * 0.05 }}
//               data-testid={`award-${i}`}
//               className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-5 hover:bg-white/10 mm-transition"
//             >
//               <div className="flex items-center justify-between">
//                 <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
//                   <Trophy className="h-4 w-4 text-[#D4AF37]" />
//                 </div>
//                 <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-white/50">
//                   {a.year}
//                 </span>
//               </div>
//               <h3 className="mt-4 text-base font-bold font-heading tracking-tight text-white">
//                 {a.title}
//               </h3>
//               <div className="text-xs text-[#D4AF37]/90 mt-1">{a.by}</div>
//               <p className="mt-3 text-xs text-white/60 leading-relaxed">
//                 {a.desc}
//               </p>
//             </motion.div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// }

import { motion } from "framer-motion";
import { Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { AWARDS } from "@/data/content";
import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

export default function Awards() {
  const autoplay = useRef(
    Autoplay({
      delay: 3500,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      loop: true,
    },
    [autoplay.current],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);

    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onInit);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  const scrollTo = useCallback(
    (index) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  return (
    <section
      id="awards"
      data-testid="awards-section"
      className="py-20 md:py-28 bg-[#0F2D52] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-0">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#1E5AA8]/20 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">
              Recognition
            </span>

            <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading tracking-tight text-white">
              Awards from our banking partners
            </h2>

            <p className="mt-3 text-sm text-white/70">
              Recognition received from partner banking institutions for
              consistent performance and quality of files facilitated.
            </p>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={scrollPrev}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>

            <button
              onClick={scrollNext}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        <div className="relative mt-14 overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-4">
            {AWARDS.map((a, i) => (
              <div
                key={a.title}
                className="
                  pl-4
                  flex-[0_0_100%]
                  md:flex-[0_0_33.3%]
                  lg:flex-[0_0_20%]
                "
              >
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`award-${i}`}
                  className="h-full rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-5 hover:bg-white/10 mm-transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                      <Trophy className="h-4 w-4 text-[#D4AF37]" />
                    </div>

                    <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-white/50">
                      {a.year}
                    </span>
                  </div>

                  <h3 className="mt-4 text-base font-bold font-heading tracking-tight text-white">
                    {a.title}
                  </h3>

                  <div className="text-xs text-[#D4AF37]/90 mt-1">{a.by}</div>

                  <p className="mt-3 text-xs text-white/60 leading-relaxed">
                    {a.desc}
                  </p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-2 md:hidden">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                index === selectedIndex
                  ? "w-8 bg-[#D4AF37]"
                  : "w-2.5 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
